import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    Calendar,
    Loader2,
    RefreshCw,
    Users,
    UserCheck,
    Wallet,
    DollarSign,
    Award,
    FileText,
    CheckCircle2,
    TrendingUp,
} from "lucide-react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
    Line,
    LineChart,
    Legend,
} from "recharts";
import { araciListeOzet } from "../lib/api";

const AYLAR = [
    { value: 0, label: "Tüm yıl" },
    { value: 1, label: "Oca" },
    { value: 2, label: "Şub" },
    { value: 3, label: "Mar" },
    { value: 4, label: "Nis" },
    { value: 5, label: "May" },
    { value: 6, label: "Haz" },
    { value: 7, label: "Tem" },
    { value: 8, label: "Ağu" },
    { value: 9, label: "Eyl" },
    { value: 10, label: "Eki" },
    { value: 11, label: "Kas" },
    { value: 12, label: "Ara" },
];

const YILLAR = [2023, 2024, 2025, 2026, 2027];

const UYARI_STIL = {
    warning: "border-amber-500/35 bg-amber-500/10 text-amber-100",
    danger: "border-rose-500/35 bg-rose-500/10 text-rose-100",
    info: "border-sky-500/35 bg-sky-500/10 text-sky-100",
};

const UYARI_IKON = {
    calendar: Calendar,
    alert: AlertTriangle,
};

function formatCurrency(val) {
    if (val == null) return "₺0,00";
    return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
    }).format(val);
}

function KpiCard({ label, value, sub, accent, icon: Icon }) {
    const border =
        accent === "violet"
            ? "border-violet-500/30 bg-violet-500/8"
            : accent === "sky"
              ? "border-sky-500/30 bg-sky-500/8"
              : accent === "gold"
                ? "border-[#D4AF37]/35 bg-[#D4AF37]/8"
                : accent === "emerald"
                  ? "border-emerald-500/30 bg-emerald-500/8"
                  : accent === "rose"
                    ? "border-rose-500/30 bg-rose-500/8"
                    : accent === "amber"
                      ? "border-amber-500/30 bg-amber-500/8"
                      : "border-white/10 bg-[#0B0F19]/60";
    const text =
        accent === "violet"
            ? "text-violet-300"
            : accent === "sky"
              ? "text-sky-300"
              : accent === "gold"
                ? "text-[#F3E5AB]"
                : accent === "emerald"
                  ? "text-emerald-300"
                  : accent === "rose"
                    ? "text-rose-300"
                    : accent === "amber"
                      ? "text-amber-300"
                      : "text-white";

    return (
        <div className={`rounded-xl border p-4 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg ${border}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                    {label}
                </div>
                {Icon && (
                    <Icon className="h-4 w-4 text-slate-500 shrink-0 opacity-70" />
                )}
            </div>
            <div className={`text-xl md:text-2xl font-semibold mt-1 ${text}`}>
                {value}
            </div>
            {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
    );
}

export default function AraciTakipPanel({
    period,
    onPeriodChange,
    onRefresh,
    listCount,
}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        const params = { yil: period.yil };
        if (period.ay) params.ay = period.ay;
        araciListeOzet(params)
            .then(setData)
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [period.yil, period.ay]);

    useEffect(() => {
        load();
    }, [load]);

    const chartData = useMemo(() => data?.aylik || [], [data]);
    const uyarilar = data?.uyarilar || [];

    return (
        <section className="relative overflow-hidden rounded-2xl border border-[#D4AF37]/20 bg-gradient-to-br from-[#121A2F] via-[#0f1424] to-[#1a1510] p-5 md:p-7 mb-6 shadow-[0_0_60px_rgba(212,175,55,0.05)]">
            <div
                className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-[#D4AF37]/8 blur-3xl"
                aria-hidden
            />
            <div
                className="pointer-events-none absolute -bottom-20 -left-12 h-48 w-48 rounded-full bg-violet-600/8 blur-3xl"
                aria-hidden
            />

            <div className="relative">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-5">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-[#F3E5AB] mb-2">
                            <Users className="h-3.5 w-3.5 text-[#D4AF37]" />
                            Aracılar ve Dosya Önerileri Takip Merkezi
                        </div>
                        <h2 className="font-display text-xl md:text-2xl text-white tracking-tight">
                            {data?.ay_label || "Özet"} · {period.yil}
                            {listCount != null && (
                                <span className="ml-2 text-sm font-mono font-normal text-slate-400 border border-white/10 rounded-lg px-2 py-0.5">
                                    {listCount} aracı listede
                                </span>
                            )}
                        </h2>
                        <p className="text-xs text-slate-500 mt-1 max-w-xl">
                            Aracı hakedişleri, ödeme durumları, yeni dosya önerileri (leads) ve aylık performans trendleri.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 shrink-0">
                        <select
                            value={period.yil}
                            onChange={(e) =>
                                onPeriodChange({
                                    ...period,
                                    yil: Number(e.target.value),
                                })
                            }
                            className="h-9 bg-[#0B0F19] border border-white/10 rounded-lg px-3 text-sm text-slate-200 focus:border-[#D4AF37]/50 focus:outline-none"
                        >
                            {YILLAR.map((y) => (
                                <option key={y} value={y}>
                                    {y}
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            onClick={() => {
                                load();
                                onRefresh?.();
                            }}
                            disabled={loading}
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-sm text-[#F3E5AB] hover:bg-[#D4AF37]/20 disabled:opacity-50"
                        >
                            {loading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <RefreshCw className="h-4 w-4" />
                            )}
                            Yenile
                        </button>
                        <Link
                            to="/yeni/araci-merkezi"
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-white/10 text-sm text-slate-300 hover:bg-white/5 hover:text-[#F3E5AB]"
                        >
                            Merkez →
                        </Link>
                    </div>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-5">
                    {AYLAR.map((a) => {
                        const active = period.ay === a.value;
                        return (
                            <button
                                key={a.value}
                                type="button"
                                onClick={() =>
                                    onPeriodChange({ ...period, ay: a.value })
                                }
                                className={`px-2.5 py-1 rounded-md text-xs font-medium transition ${
                                    active
                                        ? "bg-[#D4AF37]/80 text-black border border-[#D4AF37]/50 font-semibold"
                                        : "border border-white/10 text-slate-400 hover:bg-white/5 hover:text-slate-200"
                                }`}
                            >
                                {a.label}
                            </button>
                        );
                    })}
                </div>

                {loading && !data ? (
                    <div className="flex items-center gap-2 text-slate-400 py-8">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Özet yükleniyor…
                    </div>
                ) : (
                    <>
                        {uyarilar.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-5">
                                {uyarilar.map((u, i) => {
                                    const Icon = UYARI_IKON[u.ikon] || AlertTriangle;
                                    return (
                                        <div
                                            key={i}
                                            className={`rounded-xl border px-4 py-3 flex gap-3 ${UYARI_STIL[u.seviye] || UYARI_STIL.info}`}
                                        >
                                            <Icon className="h-5 w-5 shrink-0 mt-0.5" />
                                            <div>
                                                <div className="text-sm font-semibold">
                                                    {u.baslik}
                                                </div>
                                                <p className="text-xs opacity-85 mt-0.5 leading-relaxed">
                                                    {u.aciklama}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
                            <KpiCard
                                label="Toplam Aracı"
                                value={data?.toplam_araci ?? "—"}
                                sub={`${data?.aktif_araci ?? 0} aktif aracı`}
                                accent="violet"
                                icon={Users}
                            />
                            <KpiCard
                                label="Dönem Kaydı"
                                value={data?.donem_araci ?? "—"}
                                sub="Yeni kazanılan"
                                accent="sky"
                                icon={UserCheck}
                            />
                            <KpiCard
                                label="Toplam Hakediş"
                                value={formatCurrency(data?.toplam_hakedis)}
                                sub="Aracılara tahakkuk eden"
                                accent="gold"
                                icon={Wallet}
                            />
                            <KpiCard
                                label="Ödenen Komisyon"
                                value={formatCurrency(data?.toplam_odenen)}
                                sub="Hesaba aktarılan"
                                accent="emerald"
                                icon={CheckCircle2}
                            />
                            <KpiCard
                                label="Bekleyen Ödeme"
                                value={formatCurrency(data?.toplam_bekleyen)}
                                sub="Onay bekleyen miktar"
                                accent="rose"
                                icon={DollarSign}
                            />
                            <KpiCard
                                label="Toplam Öneri"
                                value={data?.toplam_lead ?? "—"}
                                sub={`${data?.lead_bekleyen ?? 0} onay bekleyen`}
                                accent="amber"
                                icon={FileText}
                            />
                        </div>

                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                            <div className="xl:col-span-2 rounded-xl border border-white/10 bg-[#0B0F19]/50 p-4 min-h-[17rem]">
                                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                                    {period.ay
                                        ? `${data?.ay_label} Performans Kayıtları`
                                        : `${period.yil} Aylık Yeni Aracı ve Dosya Önerisi Trendi`}
                                </h3>
                                <ResponsiveContainer width="100%" height={220} minWidth={0}>
                                    <BarChart
                                        data={
                                            period.ay
                                                ? chartData.filter(
                                                      (d) => d.ay === period.ay,
                                                  )
                                                : chartData
                                        }
                                    >
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke="#334155"
                                        />
                                        <XAxis
                                            dataKey="ay_label"
                                            tick={{ fill: "#94a3b8", fontSize: 10 }}
                                        />
                                        <YAxis
                                            tick={{ fill: "#94a3b8", fontSize: 10 }}
                                            allowDecimals={false}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                background: "#0B0F19",
                                                border: "1px solid #334155",
                                                borderRadius: 8,
                                                fontSize: 12,
                                            }}
                                        />
                                        <Legend
                                            wrapperStyle={{ fontSize: 11, paddingTop: 10 }}
                                        />
                                        <Bar
                                            dataKey="yeni_araci"
                                            name="Yeni Aracı"
                                            fill="#8B5CF6"
                                            radius={[3, 3, 0, 0]}
                                        />
                                        <Bar
                                            dataKey="yeni_lead"
                                            name="Yeni Dosya Önerisi"
                                            fill="#D4AF37"
                                            radius={[3, 3, 0, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="space-y-4">
                                {(data?.son_leadler || []).length > 0 && (
                                    <div className="rounded-xl border border-white/10 bg-[#0B0F19]/50 p-4">
                                        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                            <FileText className="h-3.5 w-3.5 text-[#D4AF37]" />
                                            Son Dosya Önerileri (Leads)
                                        </h3>
                                        <ul className="space-y-2">
                                            {data.son_leadler.map((l) => (
                                                <li
                                                    key={l.id}
                                                    className="text-xs border-b border-white/5 pb-2 last:border-0 last:pb-0"
                                                >
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-slate-200 font-medium">
                                                            {l.ad_soyad}
                                                        </span>
                                                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                                            l.durum === "Onaylandı"
                                                                ? "bg-emerald-500/10 text-emerald-400"
                                                                : l.durum === "Reddedildi"
                                                                  ? "bg-rose-500/10 text-rose-400"
                                                                  : "bg-amber-500/10 text-amber-400"
                                                        }`}>
                                                            {l.durum}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between text-slate-500 mt-1">
                                                        <span>{l.araci_ad}</span>
                                                        <span className="font-mono">{l.plaka}</span>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {(data?.en_aktif_aracilar || []).length > 0 && (
                                    <div className="rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/5 p-4">
                                        <h3 className="text-xs font-semibold text-[#F3E5AB] uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                            <Award className="h-3.5 w-3.5" />
                                            En Çok Dosya Getiren Aracılar
                                        </h3>
                                        <ul className="space-y-2">
                                            {data.en_aktif_aracilar.map((a) => (
                                                <li
                                                    key={a.id}
                                                    className="flex justify-between text-xs gap-2"
                                                >
                                                    <span className="text-slate-300 truncate font-medium">
                                                        {a.ad}
                                                    </span>
                                                    <div className="text-right shrink-0">
                                                        <span className="text-[#F3E5AB] font-mono block">
                                                            {a.muvekkil_count} dosya
                                                        </span>
                                                        <span className="text-[10px] text-slate-500 font-mono block">
                                                            {formatCurrency(a.hakedis)}
                                                        </span>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </section>
    );
}
